import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@services/auth';
import { 
	getNotificationsForUser, 
	getUnreadCountForUser, 
	markAsRead, 
	markAllAsRead, 
	deleteNotification,
	type Notification 
} from '@data/notifications';
import { getConversationById } from '@services/conversations';
import { getProjectByOfferId } from '@services/offers';
import { getProjects } from '@services/projects';
import { useI18n } from '../i18n';

export default function NotificationDropdown() {
	const { user } = useAuth();
	const { language } = useI18n();
	const navigate = useNavigate();
	const [notifications, setNotifications] = useState<Notification[]>([]);
	const [unreadCount, setUnreadCount] = useState(0);
	const [isOpen, setIsOpen] = useState(false);
	const [toastNotification, setToastNotification] = useState<Notification | null>(null);
	const dropdownRef = useRef<HTMLDivElement>(null);
	const knownNotificationIdsRef = useRef<Set<string>>(new Set());
	const toastTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
	const [isMobileViewport, setIsMobileViewport] = useState(false);
	const copy = (en: string, de: string, hr: string) => (language === 'de' ? de : language === 'hr' ? hr : en);
	const locale = language === 'de' ? 'de-DE' : language === 'hr' ? 'hr-HR' : 'en-GB';

	const translateNotificationTitle = (title: string, type: Notification['type']): string => {
		if (type === 'invite_received' && title === 'New Team Invite') return copy('New Team Invite', 'Neue Team-Einladung', 'Nova pozivnica za tim');
		if (type === 'invite_received' && title === 'New Invite') return copy('New Invite', 'Neue Einladung', 'Nova pozivnica');
		if (type === 'invite_responded' && title === 'Invite Accepted') return copy('Invite Accepted', 'Einladung angenommen', 'Pozivnica prihvaćena');
		if (type === 'invite_responded' && title === 'Invite Declined') return copy('Invite Declined', 'Einladung abgelehnt', 'Pozivnica odbijena');
		if (type === 'direct_plan_created' && title === 'New Direct Plan') return copy('New Direct Plan', 'Neuer Direktplan', 'Novi direktni plan');
		if (type === 'task_issue_reported' && title === 'New Task Issue') return copy('New Task Issue', 'Neues Aufgabenproblem', 'Novi problem na zadatku');
		if (title === 'New Offer Received') return copy('New Offer Received', 'Neues Angebot erhalten', 'Primljena nova ponuda');
		if (title === 'Offer Accepted! 🎉') return copy('Offer Accepted! 🎉', 'Angebot angenommen! 🎉', 'Ponuda prihvaćena! 🎉');
		if (title === 'Offer Not Selected') return copy('Offer Not Selected', 'Angebot nicht ausgewählt', 'Ponuda nije odabrana');
		if (title === '⚠️ Deadline Overdue') return copy('⚠️ Deadline Overdue', '⚠️ Frist überschritten', '⚠️ Rok je prekoračen');
		if (title === '⚠️ Deadline Approaching') return copy('⚠️ Deadline Approaching', '⚠️ Frist nähert sich', '⚠️ Rok se približava');
		if (title === 'Deadline Reminder') return copy('Deadline Reminder', 'Fristerinnerung', 'Podsjetnik na rok');
		if (title === 'New Plan Available') return copy('New Plan Available', 'Neuer Plan verfügbar', 'Novi plan je dostupan');
		if (title === 'Project Status Updated') return copy('Project Status Updated', 'Projektstatus aktualisiert', 'Status projekta je ažuriran');
		if (title === 'New Task Assigned') return copy('New Task Assigned', 'Neue Aufgabe zugewiesen', 'Dodijeljen je novi zadatak');
		if (title === 'Timeline Updated') return copy('Timeline Updated', 'Zeitplan aktualisiert', 'Vremenski plan ažuriran');
		const newMessageMatch = title.match(/^New message from (.+)$/);
		if (newMessageMatch) return copy(`New message from ${newMessageMatch[1]}`, `Neue Nachricht von ${newMessageMatch[1]}`, `Nova poruka od ${newMessageMatch[1]}`);
		return title;
	};

	const translateNotificationMessage = (message: string, type: Notification['type']): string => {
		if (type === 'invite_received') {
			const directPlanInviteMatch = message.match(/^(.+)\s+invited you to create a direct plan\.$/);
			if (directPlanInviteMatch) return copy(`${directPlanInviteMatch[1]} invited you to create a direct plan.`, `${directPlanInviteMatch[1]} hat Sie eingeladen, einen Direktplan zu erstellen.`, `${directPlanInviteMatch[1]} vas je pozvao/la da izradite direktni plan.`);
			const teamInviteMatch = message.match(/^(.+)\s+invited you to join their team\.$/);
			if (teamInviteMatch) return copy(`${teamInviteMatch[1]} invited you to join their team.`, `${teamInviteMatch[1]} hat Sie eingeladen, ihrem Team beizutreten.`, `${teamInviteMatch[1]} vas je pozvao/la da se pridružite njihovom timu.`);
		}
		if (type === 'invite_responded') {
			const acceptedDirectInviteMatch = message.match(/^(.+)\s+accepted your direct plan invite\.$/);
			if (acceptedDirectInviteMatch) return copy(`${acceptedDirectInviteMatch[1]} accepted your direct plan invite.`, `${acceptedDirectInviteMatch[1]} hat Ihre Direktplan-Einladung angenommen.`, `${acceptedDirectInviteMatch[1]} je prihvatio/la vašu pozivnicu za direktni plan.`);
			const declinedDirectInviteMatch = message.match(/^(.+)\s+declined your direct plan invite\.$/);
			if (declinedDirectInviteMatch) return copy(`${declinedDirectInviteMatch[1]} declined your direct plan invite.`, `${declinedDirectInviteMatch[1]} hat Ihre Direktplan-Einladung abgelehnt.`, `${declinedDirectInviteMatch[1]} je odbio/la vašu pozivnicu za direktni plan.`);
			const acceptedTeamInviteMatch = message.match(/^(.+)\s+accepted your team invite\.$/);
			if (acceptedTeamInviteMatch) return copy(`${acceptedTeamInviteMatch[1]} accepted your team invite.`, `${acceptedTeamInviteMatch[1]} hat Ihre Team-Einladung angenommen.`, `${acceptedTeamInviteMatch[1]} je prihvatio/la vašu pozivnicu za tim.`);
			const declinedTeamInviteMatch = message.match(/^(.+)\s+declined your team invite\.$/);
			if (declinedTeamInviteMatch) return copy(`${declinedTeamInviteMatch[1]} declined your team invite.`, `${declinedTeamInviteMatch[1]} hat Ihre Team-Einladung abgelehnt.`, `${declinedTeamInviteMatch[1]} je odbio/la vašu pozivnicu za tim.`);
			const acceptedAnyTeamInviteMatch = message.match(/^(.+)\s+accepted a team invite\.$/);
			if (acceptedAnyTeamInviteMatch) return copy(`${acceptedAnyTeamInviteMatch[1]} accepted a team invite.`, `${acceptedAnyTeamInviteMatch[1]} hat eine Team-Einladung angenommen.`, `${acceptedAnyTeamInviteMatch[1]} je prihvatio/la pozivnicu za tim.`);
		}
		if (type === 'direct_plan_created') {
			const newDirectPlanMatch = message.match(/^(.+)\s+created a direct plan:\s+"(.+)"\.$/);
			if (newDirectPlanMatch) return copy(`${newDirectPlanMatch[1]} created a direct plan: "${newDirectPlanMatch[2]}".`, `${newDirectPlanMatch[1]} hat einen Direktplan erstellt: "${newDirectPlanMatch[2]}".`, `${newDirectPlanMatch[1]} je izradio/la direktni plan: "${newDirectPlanMatch[2]}".`);
		}
		if (type === 'task_issue_reported') {
			const taskIssueReportedMatch = message.match(/^(.+)\s+reported an issue on a task\.$/);
			if (taskIssueReportedMatch) return copy(`${taskIssueReportedMatch[1]} reported an issue on a task.`, `${taskIssueReportedMatch[1]} hat ein Problem bei einer Aufgabe gemeldet.`, `${taskIssueReportedMatch[1]} je prijavio/la problem na zadatku.`);
		}
		const offerReceivedMatch = message.match(/^(.+)\s+sent an offer for\s+"(.+)"$/);
		if (offerReceivedMatch) return copy(`${offerReceivedMatch[1]} sent an offer for "${offerReceivedMatch[2]}"`, `${offerReceivedMatch[1]} hat ein Angebot für "${offerReceivedMatch[2]}" gesendet`, `${offerReceivedMatch[1]} je poslao/la ponudu za "${offerReceivedMatch[2]}"`);
		const offerAcceptedMatch = message.match(/^Your offer for\s+"(.+)"\s+has been accepted$/);
		if (offerAcceptedMatch) return copy(`Your offer for "${offerAcceptedMatch[1]}" has been accepted`, `Ihr Angebot für "${offerAcceptedMatch[1]}" wurde angenommen`, `Vaša ponuda za "${offerAcceptedMatch[1]}" je prihvaćena`);
		const offerRejectedMatch = message.match(/^Your offer for\s+"(.+)"\s+was not selected$/);
		if (offerRejectedMatch) return copy(`Your offer for "${offerRejectedMatch[1]}" was not selected`, `Ihr Angebot für "${offerRejectedMatch[1]}" wurde nicht ausgewählt`, `Vaša ponuda za "${offerRejectedMatch[1]}" nije odabrana`);
		const deadlinePassedMatch = message.match(/^"(.+)" deadline has passed$/);
		if (deadlinePassedMatch) return copy(`"${deadlinePassedMatch[1]}" deadline has passed`, `Die Frist für "${deadlinePassedMatch[1]}" ist abgelaufen`, `Rok za "${deadlinePassedMatch[1]}" je prošao`);
		const deadlineInMatch = message.match(/^"(.+)" deadline is in (\d+) day(?:s)?$/);
		if (deadlineInMatch) {
			const days = Number(deadlineInMatch[2]);
			return copy(
				`"${deadlineInMatch[1]}" deadline is in ${days} day${days === 1 ? '' : 's'}`,
				`Frist für "${deadlineInMatch[1]}" ist in ${days} Tag${days === 1 ? '' : 'en'}`,
				`Rok za "${deadlineInMatch[1]}" je za ${days} dan${days === 1 ? '' : 'a'}`
			);
		}
		const planAvailableMatch = message.match(/^"(.+)" in (.+) is now available$/);
		if (planAvailableMatch) return copy(`"${planAvailableMatch[1]}" in ${planAvailableMatch[2]} is now available`, `"${planAvailableMatch[1]}" in ${planAvailableMatch[2]} ist jetzt verfügbar`, `"${planAvailableMatch[1]}" u ${planAvailableMatch[2]} je sada dostupan`);
		const statusUpdatedMatch = message.match(/^"(.+)" status changed from (.+) to (.+)$/);
		if (statusUpdatedMatch) return copy(`"${statusUpdatedMatch[1]}" status changed from ${statusUpdatedMatch[2]} to ${statusUpdatedMatch[3]}`, `Status von "${statusUpdatedMatch[1]}" wurde von ${statusUpdatedMatch[2]} zu ${statusUpdatedMatch[3]} geändert`, `Status projekta "${statusUpdatedMatch[1]}" promijenjen je iz ${statusUpdatedMatch[2]} u ${statusUpdatedMatch[3]}`);
		const taskAssignedMatch = message.match(/^You've been assigned:\s+"(.+)"$/);
		if (taskAssignedMatch) return copy(`You've been assigned: "${taskAssignedMatch[1]}"`, `Ihnen wurde zugewiesen: "${taskAssignedMatch[1]}"`, `Dodijeljeno vam je: "${taskAssignedMatch[1]}"`);
		const timelineUpdatedByMatch = message.match(/^(.+)\s+updated the project timeline\.$/);
		if (timelineUpdatedByMatch) {
			return copy(
				`${timelineUpdatedByMatch[1]} updated the project timeline.`,
				`${timelineUpdatedByMatch[1]} hat den Projektzeitplan aktualisiert.`,
				`${timelineUpdatedByMatch[1]} je ažurirao/la vremenski plan projekta.`
			);
		}
		if (message === 'Project timeline has been updated. Review the new dates.') {
			return copy(
				'Project timeline has been updated. Review the new dates.',
				'Der Projektzeitplan wurde aktualisiert. Prüfen Sie die neuen Termine.',
				'Vremenski plan projekta je ažuriran. Pregledajte nove datume.'
			);
		}
		return message;
	};

	useEffect(() => {
		const updateViewport = () => setIsMobileViewport(window.innerWidth < 768);
		updateViewport();
		window.addEventListener('resize', updateViewport);
		return () => window.removeEventListener('resize', updateViewport);
	}, []);

	useEffect(() => {
		if (!user) return;

		const loadNotifications = async (showNewToast = false) => {
			try {
				const [userNotifications, unread] = await Promise.all([
					getNotificationsForUser(user.id, user.role),
					getUnreadCountForUser(user.id, user.role)
				]);
				if (showNewToast && userNotifications.length > 0) {
					const unreadNotifications = userNotifications.filter(n => !n.read);
					const newestUnread = unreadNotifications[0];
					if (newestUnread && !knownNotificationIdsRef.current.has(newestUnread.id)) {
						setToastNotification(newestUnread);
						if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
						toastTimeoutRef.current = setTimeout(() => setToastNotification(null), 5000);
					}
				}
				userNotifications.forEach(n => knownNotificationIdsRef.current.add(n.id));
				setNotifications(userNotifications);
				setUnreadCount(unread);
			} catch (error) {
				console.error('Failed to load notifications:', error);
			}
		};

		loadNotifications();

		// Listen for notification updates
		const handleUpdate = () => loadNotifications(true);
		window.addEventListener('notificationsUpdated', handleUpdate);

		// Poll for new notifications (e.g. from another tab or backend)
		const pollInterval = setInterval(() => loadNotifications(true), 45000);

		return () => {
			window.removeEventListener('notificationsUpdated', handleUpdate);
			clearInterval(pollInterval);
		};
	}, [user]);

	// Show popup when a new notification arrives for the current user (same-tab event)
	useEffect(() => {
		if (!user) return;

		const handleNewNotification = (e: Event) => {
			const ev = e as CustomEvent<Notification>;
			const notification = ev.detail;
			if (notification && notification.userId === user.id) {
				if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
				setToastNotification(notification);
				loadNotifications();
				toastTimeoutRef.current = setTimeout(() => setToastNotification(null), 5000);
			}
		};

		const loadNotifications = async () => {
			try {
				const [userNotifications, unread] = await Promise.all([
					getNotificationsForUser(user.id, user.role),
					getUnreadCountForUser(user.id, user.role)
				]);
				setNotifications(userNotifications);
				setUnreadCount(unread);
			} catch {
				// ignore
			}
		};

		window.addEventListener('newNotification', handleNewNotification);
		return () => {
			window.removeEventListener('newNotification', handleNewNotification);
			if (toastTimeoutRef.current) clearTimeout(toastTimeoutRef.current);
		};
	}, [user]);

	// Close dropdown when clicking outside
	useEffect(() => {
		const handleClickOutside = (event: MouseEvent) => {
			if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
				setIsOpen(false);
			}
		};

		if (isOpen) {
			document.addEventListener('mousedown', handleClickOutside);
		}

		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, [isOpen]);

		const resolveOfferReceivedNotificationUrl = (notification: Notification, path: string): string => {
			// Seeker should land on the offers page for the plan with this offer expanded, not just the plan page
			if (user?.role !== 'seeker' || !notification.relatedId) return path;
			const hasOffersAndOfferId = path.includes('/offers') && path.includes('offer_id=');
			if (hasOffersAndOfferId) return path;
			const planMatch = path.match(/\/seeker\/plan\/([^/?#]+)/);
			const planId = planMatch ? planMatch[1] : null;
			if (planId) {
				return `/app/seeker/plan/${planId}/offers?offer_id=${encodeURIComponent(notification.relatedId)}`;
			}
			return path;
		};

		const resolveOfferAcceptedNotificationUrl = async (notification: Notification): Promise<string> => {
			if (user?.role !== 'gardener' && user?.role !== 'company') {
				return notification.url || '/app/projects';
			}
			// API may return relatedId or related_id
			const rawRelatedId = (notification as { relatedId?: string; related_id?: string }).relatedId
				?? (notification as { relatedId?: string; related_id?: string }).related_id;
			const offerId = rawRelatedId != null ? parseInt(String(rawRelatedId), 10) : NaN;
			const userId = typeof user?.id === 'string' ? parseInt(user.id, 10) : user?.id;
			if (!userId || Number.isNaN(userId)) return notification.url || '/app/projects';

			// 1) Try direct offer -> project API
			if (!Number.isNaN(offerId)) {
				try {
					const res = await getProjectByOfferId(offerId);
					const pid = res?.project_id ?? (res as { project_id?: number })?.project_id;
					if (pid != null) return `/app/gardener/project/${pid}`;
				} catch {
					// continue to fallback
				}
			}

			// 2) Fallback: fetch gardener's projects and find by offer_id
			try {
				const projects = await getProjects({ user_id: userId });
				const offerIdNum = Number.isNaN(offerId) ? null : offerId;
				let project = offerIdNum != null
					? projects.find((p) => {
							const pOffer = p.offer_id != null ? Number(p.offer_id) : null;
							return pOffer === offerIdNum || String(p.offer_id) === String(rawRelatedId);
						})
					: null;
				// 3) If no relatedId (old notification): single project -> go there; else newest project as last resort
				if (!project && projects.length >= 1) project = projects[0];
				if (project) return `/app/gardener/project/${project.id}`;
			} catch {
				// ignore
			}
			return notification.url || '/app/projects';
		};

		const resolveMessageNotificationUrl = async (url: string): Promise<string> => {
		// Old notifications point to /app/conversation?conversation_id=X — redirect to project (or offers) page
		const match = url.match(/\/app\/conversation\?conversation_id=(\d+)/);
		if (!match || !user?.id) return url;
		const conversationId = parseInt(match[1], 10);
		const userId = typeof user.id === 'string' ? parseInt(user.id, 10) : user.id;
		if (Number.isNaN(conversationId) || Number.isNaN(userId)) return url;
		try {
			const conversation = await getConversationById(conversationId, userId);
			if (conversation.project_id) {
				const isGardenerSide = user.role === 'gardener' || user.role === 'company';
				return isGardenerSide
					? `/app/gardener/project/${conversation.project_id}?tab=messages`
					: `/app/seeker/project/${conversation.project_id}`;
			}
			if (conversation.plan_id != null && user.role === 'seeker') {
				return `/app/seeker/plan/${conversation.plan_id}/offers?offer_id=${conversation.offer_id}`;
			}
			if (user.role === 'gardener' || user.role === 'company') {
				return '/app/projects?tab=offers';
			}
		} catch {
			// fallback to original url
		}
		return url;
	};

	const handleNotificationClick = async (notification: Notification) => {
		if (!notification.read) {
			try {
				await markAsRead(notification.id);
				setUnreadCount(prev => Math.max(0, prev - 1));
				setNotifications(prev => prev.map(n => 
					n.id === notification.id ? { ...n, read: true } : n
				));
			} catch (error) {
				console.error('Failed to mark notification as read:', error);
			}
		}
		
		let path = notification.url || '';
		if (path.startsWith('http')) {
			try {
				const u = new URL(path);
				path = u.pathname + u.search;
			} catch {
				// keep path as-is
			}
		}
		if (!path && notification.type === 'offer_accepted') path = '/app/projects';
		if (!path && notification.type === 'invite_received') {
			path = user?.role === 'seeker' ? '/app/seeker/plans' : '/app/settings';
		}
		if (!path && notification.type === 'direct_plan_created') path = '/app/seeker/plans';
		if (!path && notification.type === 'task_issue_reported') path = '/app/tasks';
		if (!path && notification.type === 'project_replanned') path = '/app/projects';
		if (!path && notification.type === 'invite_responded') path = '/app/team';
		if (path) {
			if (notification.type === 'new_message' && path.includes('/app/conversation?')) {
				path = await resolveMessageNotificationUrl(path);
			} else if (notification.type === 'offer_accepted' && !path.includes('/app/gardener/project/')) {
				path = await resolveOfferAcceptedNotificationUrl(notification);
			} else if (notification.type === 'offer_received') {
				path = resolveOfferReceivedNotificationUrl(notification, path);
			}
			navigate(path);
			setIsOpen(false);
		}
	};

	const handleMarkAllAsRead = async () => {
		if (!user) return;
		try {
			await markAllAsRead(user.id);
			setUnreadCount(0);
			setNotifications(prev => prev.map(n => ({ ...n, read: true })));
		} catch (error) {
			console.error('Failed to mark all as read:', error);
		}
	};

	const handleDelete = async (e: React.MouseEvent, notificationId: string) => {
		e.stopPropagation();
		const wasUnread = notifications.find(n => n.id === notificationId && !n.read);
		try {
			await deleteNotification(notificationId);
			setNotifications(prev => prev.filter(n => n.id !== notificationId));
			if (wasUnread) {
				setUnreadCount(prev => Math.max(0, prev - 1));
			}
		} catch (error) {
			console.error('Failed to delete notification:', error);
		}
	};

	const getNotificationIcon = (type: Notification['type']) => {
		switch (type) {
			case 'offer_received':
				return 'bi-envelope';
			case 'offer_accepted':
				return 'bi-check-circle';
			case 'offer_rejected':
				return 'bi-x-circle';
			case 'new_message':
				return 'bi-chat-dots';
			case 'deadline_near':
			case 'deadline_overdue':
				return 'bi-clock';
			case 'plan_published':
				return 'bi-journal-text';
			case 'project_status_changed':
				return 'bi-arrow-repeat';
			case 'task_assigned':
				return 'bi-check2-square';
			case 'invite_received':
				return 'bi-person-plus';
			case 'invite_responded':
				return 'bi-person-check';
			case 'direct_plan_created':
				return 'bi-journal-plus';
			case 'task_issue_reported':
				return 'bi-exclamation-triangle';
			case 'project_replanned':
				return 'bi-arrow-repeat';
			default:
				return 'bi-bell';
		}
	};

	const getNotificationColor = (type: Notification['type']) => {
		switch (type) {
			case 'offer_accepted':
				return 'text-success';
			case 'offer_rejected':
				return 'text-danger';
			case 'deadline_overdue':
				return 'text-danger';
			case 'deadline_near':
				return 'text-warning';
			case 'new_message':
				return 'text-primary';
			case 'task_issue_reported':
				return 'text-danger';
			case 'invite_received':
			case 'invite_responded':
			case 'direct_plan_created':
			case 'project_replanned':
				return 'text-info';
			default:
				return 'text-primary';
		}
	};

	if (!user) return null;

	const unreadNotifications = notifications.filter(n => !n.read);
	const readNotifications = notifications.filter(n => n.read);
	const notificationPanelStyle = isMobileViewport
		? {
				width: 'min(320px, calc(100vw - 2.25rem))',
				maxWidth: 'min(320px, calc(100vw - 2.25rem))',
				maxHeight: 'min(440px, calc(100dvh - 5.5rem))',
				overflowY: 'auto' as const,
				overflowX: 'hidden' as const,
				zIndex: 1200,
				left: 'auto',
				right: '0.5rem',
				top: '3.75rem',
				marginTop: 0,
				display: 'block',
				position: 'fixed' as const,
				borderRadius: '0.75rem'
			}
		: {
				width: 'min(360px, calc(100vw - 2rem))',
				maxWidth: '360px',
				maxHeight: 'min(500px, calc(100vh - 8rem))',
				overflowY: 'auto' as const,
				overflowX: 'hidden' as const,
				zIndex: 1050,
				right: 0,
				left: 'auto',
				top: '100%',
				marginTop: '0.5rem',
				display: 'block',
				position: 'absolute' as const,
				borderRadius: undefined
			};

	return (
		<div className="position-relative d-flex align-items-center" ref={dropdownRef} style={{ overflow: 'visible' }}>
			<button
				className="btn btn-outline-secondary btn-sm position-relative"
				type="button"
				onClick={(e) => {
					e.stopPropagation();
					setIsOpen(!isOpen);
				}}
				aria-expanded={isOpen}
				aria-label={copy('Notifications', 'Benachrichtigungen', 'Obavijesti')}
				style={{ overflow: 'visible' }}
			>
				<i className="bi bi-bell"></i>
				{unreadCount > 0 && (
					<span
						className="position-absolute badge rounded-pill bg-danger notification-badge"
						style={{
							fontSize: '0.65rem',
							top: 0,
							right: 0,
							transform: 'translate(50%, -50%)',
							minWidth: '1.1rem',
							height: '1.1rem',
							display: 'inline-flex',
							alignItems: 'center',
							justifyContent: 'center',
							padding: '0 0.35rem',
							lineHeight: 1,
						}}
					>
						{unreadCount > 99 ? '99+' : unreadCount}
					</span>
				)}
			</button>
			{isOpen && (
				<>
				{isMobileViewport && (
					<button
						type="button"
						aria-label={copy('Close notifications', 'Benachrichtigungen schließen', 'Zatvori obavijesti')}
						onClick={() => setIsOpen(false)}
						style={{
							position: 'fixed',
							inset: 0,
							background: 'rgba(0, 0, 0, 0.2)',
							border: 'none',
							padding: 0,
							margin: 0,
							zIndex: 1190
						}}
					/>
				)}
				<div 
					className={`dropdown-menu border-0 shadow-lg ${isMobileViewport ? '' : 'dropdown-menu-end position-absolute'}`}
					style={notificationPanelStyle}
				>
					<div className="d-flex align-items-center justify-content-between px-3 py-2 border-bottom">
						<h6 className="mb-0 fw-semibold">{copy('Notifications', 'Benachrichtigungen', 'Obavijesti')}</h6>
						{unreadCount > 0 && (
							<button
								className="btn btn-link btn-sm text-decoration-none p-0"
								onClick={handleMarkAllAsRead}
								type="button"
							>
								<small>{copy('Mark all as read', 'Alle als gelesen markieren', 'Označi sve kao pročitano')}</small>
							</button>
						)}
					</div>
					
					{notifications.length === 0 ? (
						<div className="px-3 py-4 text-center text-muted">
							<i className="bi bi-bell-slash fs-4 d-block mb-2"></i>
							<small>{copy('No notifications', 'Keine Benachrichtigungen', 'Nema obavijesti')}</small>
						</div>
					) : (
						<>
							{unreadNotifications.length > 0 && (
								<>
									<div className="px-3 py-2 bg-light border-bottom">
										<small className="text-muted fw-semibold text-uppercase">{copy('Unread', 'Ungelesen', 'Nepročitano')}</small>
									</div>
									{unreadNotifications.map((notification) => (
										<button
											key={notification.id}
											className="dropdown-item d-flex align-items-start gap-2 p-3 border-bottom position-relative"
											onClick={() => handleNotificationClick(notification)}
											type="button"
											style={{ backgroundColor: notification.read ? 'transparent' : '#f8f9fa', minWidth: 0 }}
										>
											<i className={`bi ${getNotificationIcon(notification.type)} ${getNotificationColor(notification.type)} mt-1 flex-shrink-0`}></i>
											<div className="flex-grow-1 text-start" style={{ minWidth: 0, overflow: 'hidden' }}>
												<div className="fw-semibold small text-break">{translateNotificationTitle(notification.title, notification.type)}</div>
												<div className="text-muted small text-break" style={{ wordBreak: 'break-word' }}>{translateNotificationMessage(notification.message, notification.type)}</div>
												<div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '4px' }}>
													{new Date(notification.createdAt).toLocaleDateString(locale)} {new Date(notification.createdAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
												</div>
											</div>
											<div className="d-flex align-items-start gap-1 flex-shrink-0">
												{!notification.read && (
													<span className="badge bg-primary rounded-pill mt-1" style={{ width: '8px', height: '8px', padding: 0 }}></span>
												)}
												<button
													className="btn btn-link btn-sm text-muted p-0"
													onClick={(e) => handleDelete(e, notification.id)}
													type="button"
													aria-label={copy('Delete notification', 'Benachrichtigung löschen', 'Izbriši obavijest')}
													style={{ lineHeight: 1 }}
												>
													<i className="bi bi-x"></i>
												</button>
											</div>
										</button>
									))}
								</>
							)}
							
							{readNotifications.length > 0 && unreadNotifications.length > 0 && (
								<div className="px-3 py-2 bg-light border-bottom">
									<small className="text-muted fw-semibold text-uppercase">{copy('Read', 'Gelesen', 'Pročitano')}</small>
								</div>
							)}
							
							{readNotifications.map((notification) => (
								<button
									key={notification.id}
									className="dropdown-item d-flex align-items-start gap-2 p-3 border-bottom position-relative"
									onClick={() => handleNotificationClick(notification)}
									type="button"
									style={{ minWidth: 0 }}
								>
									<i className={`bi ${getNotificationIcon(notification.type)} text-muted mt-1 flex-shrink-0`}></i>
									<div className="flex-grow-1 text-start" style={{ minWidth: 0, overflow: 'hidden' }}>
										<div className="fw-semibold small text-muted text-break">{translateNotificationTitle(notification.title, notification.type)}</div>
										<div className="text-muted small text-break" style={{ wordBreak: 'break-word' }}>{translateNotificationMessage(notification.message, notification.type)}</div>
										<div className="text-muted" style={{ fontSize: '0.7rem', marginTop: '4px' }}>
											{new Date(notification.createdAt).toLocaleDateString(locale)} {new Date(notification.createdAt).toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' })}
										</div>
									</div>
									<button
										className="btn btn-link btn-sm text-muted p-0 flex-shrink-0"
										onClick={(e) => handleDelete(e, notification.id)}
										type="button"
										aria-label={copy('Delete notification', 'Benachrichtigung löschen', 'Izbriši obavijest')}
										style={{ lineHeight: 1 }}
									>
										<i className="bi bi-x"></i>
									</button>
								</button>
							))}
						</>
					)}
				</div>
				</>
			)}
			{toastNotification && (
				<div
					className="notification-toast shadow-lg border-0 rounded position-fixed d-flex align-items-start gap-2 p-3 animate-notification-toast"
					role="alert"
					style={{
						top: '4.5rem',
						right: '1.5rem',
						zIndex: 1060,
						minWidth: '280px',
						maxWidth: 'min(360px, calc(100vw - 2rem))',
						background: '#fff',
						borderLeft: '4px solid var(--bs-primary, #2f9e59)',
					}}
				>
					<i className={`bi ${getNotificationIcon(toastNotification.type)} ${getNotificationColor(toastNotification.type)} flex-shrink-0 mt-1`} style={{ fontSize: '1.25rem' }}></i>
					<div className="flex-grow-1 min-w-0">
						<div className="fw-semibold small">{translateNotificationTitle(toastNotification.title, toastNotification.type)}</div>
						<div className="text-muted small text-break" style={{ wordBreak: 'break-word' }}>{translateNotificationMessage(toastNotification.message, toastNotification.type)}</div>
						<button
							type="button"
							className="btn btn-sm btn-outline-primary mt-2"
							onClick={async () => {
								const notif = toastNotification;
								setToastNotification(null);
								if (notif.url) {
									let path = notif.url;
									if (path.startsWith('http')) {
										try {
											const u = new URL(path);
											path = u.pathname + u.search;
										} catch {
											// keep as-is
										}
									}
									if (notif.type === 'new_message' && path.includes('/app/conversation?')) {
										path = await resolveMessageNotificationUrl(path);
									} else if (notif.type === 'offer_accepted' && !path.includes('/app/gardener/project/')) {
										path = await resolveOfferAcceptedNotificationUrl(notif);
									} else if (notif.type === 'offer_received') {
										path = resolveOfferReceivedNotificationUrl(notif, path);
									}
									navigate(path);
								} else {
									setIsOpen(true);
								}
							}}
						>
							{copy('View', 'Ansehen', 'Prikaži')}
						</button>
					</div>
					<button
						type="button"
						className="btn btn-link btn-sm text-muted p-0 align-self-start"
						onClick={() => setToastNotification(null)}
						aria-label={copy('Dismiss', 'Schließen', 'Zatvori')}
					>
						<i className="bi bi-x"></i>
					</button>
				</div>
			)}
		</div>
	);
}

